import type { QuestionDetail } from "@models/Dataset";
import type { DatasetMetric, MetricBase, MetricRepresentedResult } from "@models/Metric";
import { DEFAULT_VISUALIZATION_BASE, type VisualizationBase } from "@models/VisualizationBase";
import { getBrandCandidates, toMatchableCategories } from "@utils/scorecard-utils";

/**
 * Whether generating this metric's represented-result candidates requires
 * fetching the underlying QuestionDetail — false when the metric's saved
 * focus already carries everything needed (Multiple/Numeric variable lists,
 * or a single picked value/variable label). Shared baseline used by every
 * consumer that only needs *labels* (Scorecard's brand mapping); a consumer
 * that also needs raw value *codes* for its own request (Bar Chart's VALUE
 * focus) needs a stricter, chart-specific predicate — see
 * bar-chart-config/metric-source/bar-chart-metric-utils.ts.
 */
export function metricNeedsQuestionDetailFetch(metric: DatasetMetric): boolean {
  const source = metric.source;
  if (!source) return false;
  switch (source.kind) {
    case "single_one_dimensional":
      return source.focus.mode === "all";
    case "single_grid_value_focus":
    case "single_grid_variable_focus":
      return true;
    case "multiple_one_dimensional":
    case "numeric":
      return false;
  }
}

/**
 * Adapts a saved Dataset Metric's definition into the "represented results" a
 * visualization's bars/rows/mappings can bind to. Reused by Scorecard's Brand
 * Mapping step and Bar Chart's bar generation — see the PRD's shared
 * eligibility/mapping table:
 * - One-dimensional Single, all categories: one candidate per response value.
 * - One-dimensional Single, one selected value: that single value.
 * - One-dimensional Multiple: one candidate per selected response variable.
 * - Single grid, Value Focus: one candidate per grid variable (row).
 * - Single grid, Variable Focus: one candidate per response value (column)
 *   for the saved variable.
 * - Number/Float: one candidate per selected numeric variable.
 * questionDetail is only required for the branches that need it (see
 * metricNeedsQuestionDetailFetch) — passing null for those returns [].
 */
export function getMetricResultCandidates(
  metric: DatasetMetric,
  questionDetail?: QuestionDetail | null,
): MetricRepresentedResult[] {
  const source = metric.source;
  if (!source) return [];

  switch (source.kind) {
    case "single_one_dimensional": {
      if (source.focus.mode === "rollup") {
        // Scorecard excludes this case entirely (isMetricEligibleForBrandMapping
        // in scorecard-config). Bar Chart does not: it behaves exactly like a
        // Question-path chart-level Category Rollup — the untouched source
        // categories stay as their own bars, plus one merged bar for the
        // rollup, not narrowed down to just the rollup (confirmed with the
        // client — matches Question-path rollup display).
        if (!questionDetail) return [];
        const rollup = source.focus.rollup;
        const rolledUpIds = new Set(rollup.sourceIds);
        const untouched = toMatchableCategories(getBrandCandidates(questionDetail)).filter(
          (c) => !rolledUpIds.has(c.id),
        );
        return [
          ...untouched,
          { id: rollup.id, label: rollup.name, matchLabels: [rollup.name] },
        ];
      }
      if (source.focus.mode === "one") {
        return [
          {
            id: source.focus.valueId,
            label: source.focus.valueLabel,
            matchLabels: [source.focus.valueLabel],
          },
        ];
      }
      // mode === "all"
      if (!questionDetail) return [];
      return toMatchableCategories(getBrandCandidates(questionDetail));
    }

    case "multiple_one_dimensional":
      return source.focus.variableIds.map((id) => ({
        id,
        label: source.focus.variableLabels[id] ?? id,
        matchLabels: [source.focus.variableLabels[id] ?? id],
      }));

    case "single_grid_value_focus": {
      if (!questionDetail) return [];
      return toMatchableCategories(getBrandCandidates(questionDetail));
    }

    case "single_grid_variable_focus": {
      if (!questionDetail) return [];
      return (questionDetail.values ?? []).map((v) => ({
        id: v.id,
        label: v.title || v.label,
        matchLabels: Array.from(new Set([v.title, v.label].filter((l): l is string => !!l))),
      }));
    }

    case "numeric":
      return source.focus.variableIds.map((id) => ({
        id,
        label: source.focus.variableLabels[id] ?? id,
        matchLabels: [source.focus.variableLabels[id] ?? id],
      }));
  }
}

/** Case-insensitive substring match against the metric's name, source
 *  question label, and source question title — alphabetical by name. */
export function filterAndSortDatasetMetrics(
  metrics: DatasetMetric[],
  searchTerm: string,
): DatasetMetric[] {
  const term = searchTerm.trim().toLowerCase();
  const filtered = term
    ? metrics.filter((metric) => {
        const haystacks = [
          metric.name,
          metric.source?.question.questionLabel,
          metric.source?.question.questionTitle,
        ];
        return haystacks.some((h) => h?.toLowerCase().includes(term));
      })
    : metrics;
  return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
}

/** Translates a saved Metric's own Base into the VisualizationBase shape
 *  resolveVisualizationBaseFilters expects — the two are structurally
 *  compatible by design (MetricBase "mirrors VisualizationBase's custom/
 *  preconfigured split"), this just bridges the small shape differences. */
export function metricBaseToVisualizationBase(base: MetricBase): VisualizationBase {
  switch (base.mode) {
    case "all_eligible":
      return { ...DEFAULT_VISUALIZATION_BASE };
    case "custom":
      return { enabled: true, name: base.name, conditions: base.conditions, source: "custom" };
    case "preconfigured":
      return {
        enabled: true,
        name: "",
        conditions: [],
        source: "preconfigured",
        preconfiguredBaseId: base.preconfiguredBaseId,
        preconfiguredBaseName: base.preconfiguredBaseName,
      };
  }
}

/**
 * Narrows a fetched chart response down to a metric's own represented
 * results — a no-op for every source kind whose request already returns
 * exactly the right set via request-level focus/rollup, and the one real
 * narrowing for source kinds with no such request-level focus (e.g.
 * multiple_one_dimensional's selected-variable subset). Also guards every
 * other kind against dataset drift (a source question gaining a new
 * value/variable the metric never selected). Chart-agnostic — shared by
 * Bar Chart and Line Chart.
 */
export function filterFetchedDataToMetricResults<T extends { variableLabel?: string }>(
  data: T[],
  representedResults: MetricRepresentedResult[],
): T[] {
  if (representedResults.length === 0) return data;
  const matchSet = new Set(
    representedResults.flatMap((r) => r.matchLabels.map((l) => l.toLowerCase())),
  );
  return data.filter((item) => matchSet.has((item.variableLabel ?? "").toLowerCase()));
}
